/**
 * SAP Design System Theme Integration
 * Provides seamless integration between SAP design tokens and MUI theme
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS, SAP_TRANSITIONS } from './sapDesignSystem';

// Enhanced theme options with SAP design system integration
export const createSAPTheme = (options: ThemeOptions = {}) => {
  const sapTheme = createTheme({
    palette: {
      primary: {
        main: SAP_COLORS.sapBlue,
        dark: SAP_COLORS.sapDarkBlue,
        light: SAP_COLORS.sapLightBlue,
        contrastText: SAP_COLORS.sapWhite,
      },
      secondary: {
        main: SAP_COLORS.sapGray6,
        dark: SAP_COLORS.sapGray7,
        light: SAP_COLORS.sapGray5,
        contrastText: SAP_COLORS.sapWhite,
      },
      success: {
        main: SAP_COLORS.sapGreen,
        dark: '#2E7D32',
        light: SAP_COLORS.sapLightGreen,
        contrastText: SAP_COLORS.sapWhite,
      },
      warning: {
        main: SAP_COLORS.sapOrange,
        dark: '#D84315',
        light: SAP_COLORS.sapLightOrange,
        contrastText: SAP_COLORS.sapWhite,
      },
      error: {
        main: SAP_COLORS.sapRed,
        dark: '#C62828',
        light: SAP_COLORS.sapLightRed,
        contrastText: SAP_COLORS.sapWhite,
      },
      info: {
        main: SAP_COLORS.sapBlue,
        dark: SAP_COLORS.sapDarkBlue,
        light: SAP_COLORS.sapLightBlue,
        contrastText: SAP_COLORS.sapWhite,
      },
      background: {
        default: SAP_COLORS.sapGray1,
        paper: SAP_COLORS.sapWhite,
      },
      text: {
        primary: SAP_COLORS.sapGray7,
        secondary: SAP_COLORS.sapGray5,
        disabled: SAP_COLORS.sapGray4,
      },
      divider: SAP_COLORS.sapGray2,
      grey: {
        50: SAP_COLORS.sapGray1,
        100: SAP_COLORS.sapGray2,
        200: SAP_COLORS.sapGray3,
        300: SAP_COLORS.sapGray4,
        400: SAP_COLORS.sapGray5,
        500: SAP_COLORS.sapGray6,
        600: SAP_COLORS.sapGray7,
        700: SAP_COLORS.sapGray7,
        800: SAP_COLORS.sapGray7,
        900: SAP_COLORS.sapBlack,
      },
    },
    typography: {
      fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
      h1: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize['4xl'],
        fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
        color: SAP_COLORS.sapGray7,
      },
      h2: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize['3xl'],
        fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
        color: SAP_COLORS.sapGray7,
      },
      h3: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize['2xl'],
        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
        color: SAP_COLORS.sapGray7,
      },
      h4: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.xl,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
        color: SAP_COLORS.sapGray7,
      },
      h5: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.lg,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
        color: SAP_COLORS.sapGray7,
      },
      h6: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.md,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
        color: SAP_COLORS.sapGray7,
      },
      body1: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.md,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
        color: SAP_COLORS.sapGray7,
      },
      body2: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.sm,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
        color: SAP_COLORS.sapGray6,
      },
      caption: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.xs,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
        lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
        color: SAP_COLORS.sapGray5,
      },
      button: {
        fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
        fontSize: SAP_TYPOGRAPHY.fontSize.sm,
        fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
        textTransform: 'none',
        letterSpacing: SAP_TYPOGRAPHY.letterSpacing.normal,
      },
    },
    spacing: 8, // 8px base unit
    shape: {
      borderRadius: parseInt(SAP_SPACING.component.borderRadius.replace('rem', '')) * 16, // Convert rem to px
    },
    // MUI expects a tuple of 25 string shadows; ensure strict string typing
    shadows: (
      [
        'none',
        String(SAP_SHADOWS.xs),
        String(SAP_SHADOWS.sm),
        String(SAP_SHADOWS.md),
        String(SAP_SHADOWS.lg),
        String(SAP_SHADOWS.xl),
        String(SAP_SHADOWS['2xl']),
        String(SAP_SHADOWS.card),
        String(SAP_SHADOWS.modal),
        ...Array(16).fill(String(SAP_SHADOWS.lg)), // Fill remaining to reach 25
      ] as string[]
    ) as unknown as any,
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    components: {
      // Global component overrides
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontSize: SAP_TYPOGRAPHY.fontSize.md,
            lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
            color: SAP_COLORS.sapGray7,
            backgroundColor: SAP_COLORS.sapGray1,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            textTransform: 'none',
            borderRadius: SAP_SPACING.component.borderRadius,
            transition: SAP_TRANSITIONS.fast,
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: SAP_SHADOWS.sm,
            '&:hover': {
              boxShadow: SAP_SHADOWS.md,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            borderRadius: SAP_SPACING.component.borderRadius,
            transition: SAP_TRANSITIONS.normal,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: SAP_COLORS.sapBlue,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: SAP_COLORS.sapBlue,
              borderWidth: '2px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: SAP_SPACING.component.borderRadius,
            boxShadow: SAP_SHADOWS.sm,
            transition: SAP_TRANSITIONS.normal,
            '&:hover': {
              boxShadow: SAP_SHADOWS.md,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: SAP_SPACING.component.borderRadius,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            borderRadius: SAP_SPACING.component.borderRadius,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontSize: SAP_TYPOGRAPHY.fontSize.xs,
            backgroundColor: SAP_COLORS.sapGray7,
            color: SAP_COLORS.sapWhite,
            borderRadius: SAP_SPACING.component.borderRadius,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: SAP_SPACING.component.borderRadius,
            boxShadow: SAP_SHADOWS.modal,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: SAP_SPACING.component.borderRadius,
            boxShadow: SAP_SHADOWS.lg,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontSize: SAP_TYPOGRAPHY.fontSize.sm,
            transition: SAP_TRANSITIONS.fast,
            '&:hover': {
              backgroundColor: SAP_COLORS.hover,
            },
            '&.Mui-selected': {
              backgroundColor: SAP_COLORS.sapLightBlue,
              color: SAP_COLORS.sapBlue,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: SAP_COLORS.sapGray1,
            '& .MuiTableCell-head': {
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              fontSize: SAP_TYPOGRAPHY.fontSize.sm,
              color: SAP_COLORS.sapGray7,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
            fontSize: SAP_TYPOGRAPHY.fontSize.sm,
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: SAP_COLORS.hover,
            },
          },
        },
      },
    },
    ...options, // Allow custom overrides
  });

  return sapTheme;
};

// Default SAP theme
export const sapTheme = createSAPTheme();

// Theme variants
export const createSAPThemeVariant = (variant: 'light' | 'dark' | 'high-contrast') => {
  const baseTheme = createSAPTheme();
  
  switch (variant) {
    case 'dark':
      return createSAPTheme({
        palette: {
          mode: 'dark',
          primary: {
            main: SAP_COLORS.sapBlue,
            dark: SAP_COLORS.sapDarkBlue,
            light: SAP_COLORS.sapLightBlue,
            contrastText: SAP_COLORS.sapWhite,
          },
          background: {
            default: '#1a1a1a',
            paper: '#2d2d2d',
          },
          text: {
            primary: SAP_COLORS.sapWhite,
            secondary: SAP_COLORS.sapGray4,
          },
        },
      });
    
    case 'high-contrast':
      return createSAPTheme({
        palette: {
          primary: {
            main: '#000000',
            dark: '#000000',
            light: '#333333',
            contrastText: SAP_COLORS.sapWhite,
          },
          background: {
            default: SAP_COLORS.sapWhite,
            paper: SAP_COLORS.sapWhite,
          },
          text: {
            primary: '#000000',
            secondary: '#333333',
          },
        },
      });
    
    default:
      return baseTheme;
  }
};

// Export theme utilities
export const themeUtils = {
  // Get color by semantic name
  getColor: (semantic: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info') => {
    const colorMap = {
      primary: SAP_COLORS.sapBlue,
      secondary: SAP_COLORS.sapGray6,
      success: SAP_COLORS.sapGreen,
      warning: SAP_COLORS.sapOrange,
      error: SAP_COLORS.sapRed,
      info: SAP_COLORS.sapBlue,
    };
    return colorMap[semantic];
  },
  
  // Get spacing value
  getSpacing: (multiplier: number) => `${multiplier * 8}px`,
  
  // Get shadow by elevation
  getShadow: (elevation: number) => {
    const shadows = [
      'none',
      SAP_SHADOWS.xs,
      SAP_SHADOWS.sm,
      SAP_SHADOWS.md,
      SAP_SHADOWS.lg,
      SAP_SHADOWS.xl,
    ];
    return shadows[Math.min(elevation, shadows.length - 1)];
  },
  
  // Get transition duration
  getTransition: (type: 'fast' | 'normal' | 'slow') => {
    const transitions = {
      fast: SAP_TRANSITIONS.fast,
      normal: SAP_TRANSITIONS.normal,
      slow: SAP_TRANSITIONS.slow,
    };
    return transitions[type];
  },
};

// Export types
export type SAPTheme = ReturnType<typeof createSAPTheme>;
export type SAPThemeVariant = 'light' | 'dark' | 'high-contrast';
